/**
 * @fileoverview Table Body component in loading state.
 * Renders <tbody> and its contents when the loading prop is truthy.
 * @author Derek Seo (dseo@yext.com)
 */
// @ts-nocheck
goog.declareModuleId('yext.ui.components.table.tablebody.loadingbody');

import {Checkbox} from '/ui/components/checkbox/checkbox';
import {ContentSkeleton} from '/ui/components/contentskeleton/contentskeleton';
import {CursorPaginationControls} from '/ui/components/cursorpagination/cursorpaginationcontrols';
import {PaginationControls} from '/ui/components/pagination/paginationcontrols';
import {selectCell} from '/ui/components/table/body/selectcell';
import * as styles from '/ui/components/table/body/tablebody.module.scss';
import * as BodyTypes from '/ui/components/table/body/types';
import {INTERNAL_COL_IDS} from '/ui/components/table/common';
import * as Types from '/ui/components/table/types';
import {logError} from '/ui/lib/errors';

/**
* @param {!BodyTypes.TableBodyProps} tableBodyProps
* @return {!Array<React.ReactElement>}
*/
export function loadingBodyTRs(tableBodyProps) {
  let {
    dataRows,
    pagination, // Read this to calculate the default number of loading rows when dataRows is empty.
    noDataNumLoadingRows,
  } = tableBodyProps;
  if (dataRows.length === 0) {
    const numLoadingRows = getDefaultNumLoadingRows(noDataNumLoadingRows, pagination);
    dataRows = generateLoadingDataRows(numLoadingRows);
  }

  return dataRows.map(dataRow => loadingTR(dataRow, /** @type {!BodyTypes.TableBodyProps} */ ({...tableBodyProps, dataRows})));
}

const DEFAULT_NO_DATA_NUM_LOADING_ROWS = 25;
/**
 *
 * @param {number | undefined} noDataNumLoadingRows
 * @param {React.ReactElement | undefined} pagination
 * @returns {number}
 * @suppress {checkTypes}
 * Our React externs seem incomplete, so Closure doesn't like references to
 * pagination.type and pagination.props.
 * TODO (UX-1938): Suppression can hopefully be removed after UX-1938
 */
function getDefaultNumLoadingRows(noDataNumLoadingRows, pagination) {
  if (noDataNumLoadingRows) {
    return noDataNumLoadingRows;
  }

  if (pagination) {
    try {
      const paginationComponentType = pagination.type;
      switch (paginationComponentType) {
        case PaginationControls:
          return pagination.props.paginationData.limit;
        case CursorPaginationControls:
          return pagination.props.limit;
        default:
          throw new Error('pagination prop was neither PaginationControls nor CursorPaginationControls.');
      }
    } catch (e) {
      logError(`<TableBody/>: Error parsing pagination props:\n${e}`);
    }
  }

  return DEFAULT_NO_DATA_NUM_LOADING_ROWS;
}

/**
 *
 * @param {number} numRows
 * @returns {!Array<Types.Row>}
 */
function generateLoadingDataRows(numRows) {
  const dataRows = Array(numRows);
  for (const index of dataRows.keys()) {
    dataRows[index] = {id: `_dummyRow_${index}`};
  }
  return dataRows;
}

const defaultLoadingCell = (
  <ContentSkeleton/>
);

/**
 *
 * @param {!Types.Row} dataRow
 * @param {!BodyTypes.TableBodyProps} tableBodyProps
 * @returns {React.ReactElement}
 */
function loadingTR(dataRow, tableBodyProps) {
  const {
    getCellLoadingContents,
    columns,
  } = tableBodyProps;
  const getColCell = col => {
    if (col.id === INTERNAL_COL_IDS.SELECT) {
      return selectCell(dataRow, tableBodyProps);
    }
    let contents = getCellLoadingContents && getCellLoadingContents(col.id, dataRow);
    if (!contents) {
      contents = defaultLoadingCell;
    }
    return (
      <td className={styles.cell} key={col.id}>
        <div className={styles.cellContents}>
          {contents}
        </div>
      </td>
    );
  };

  return (
    <tr key={dataRow.id} aria-hidden>
      {columns.map(getColCell)}
    </tr>
  );
}
